import { get, isNil, isObject, isString } from 'lodash';
import { Dispatch } from 'redux';
import { UNEXPECTED_ERROR_LABEL } from '@constants/common';
import { HTTPStatusCode } from '@constants/enum/common';
import { SilentError } from '@constants/error';
import { addNotification, addSequentialNotification } from '@redux/actions/notificationActions';
import { PartialBy } from '@redux/types/commonTypes';
import {
  INotification,
  NotificationDuration,
  NotificationMode,
  NotificationType,
} from '@redux/types/types';

const SILENT_5XX_ERRORS = import.meta.env.VITE_SILENT_5XX_ERRORS === 'true';
const SILENT_404_ERRORS = import.meta.env.VITE_SILENT_404_ERRORS === 'true';

interface ErrorNotificationOptions {
  sequential?: boolean;
  duration?: NotificationDuration;
  mode?: NotificationMode;
  title?: string;
  type?: NotificationType;
}

const DEFAULT_OPTIONS: ErrorNotificationOptions = {
  sequential: false,
  type: NotificationType.Error,
  duration: NotificationDuration.default,
  mode: NotificationMode.Message,
};

export const normalizeErrorMessage = (error: unknown): string => {
  if (isObject(error) && !isNil(error)) {
    const apiError = get(error, 'response.data.errors[0]');
    if (isString(apiError)) return apiError;

    // Handle different api error structures,
    // - HTTPValidationSchema errors (msg)
    const httpValidationSchemaError = get(apiError, 'msg');
    if (httpValidationSchemaError) return httpValidationSchemaError;

    const errorMessage = get(error, 'message');
    if (errorMessage) return errorMessage;
  }

  if (error instanceof Error) return error.message;

  if (isString(error)) return error;

  return UNEXPECTED_ERROR_LABEL;
};

export const getStatusCode = (error: unknown): number => {
  return get(error, 'response.status', HTTPStatusCode.INTERNAL_SERVER_ERROR);
};

export const handleErrorNotification = (
  error: unknown,
  dispatch: Dispatch,
  options: ErrorNotificationOptions = DEFAULT_OPTIONS,
): void => {
  // Don't show notification for silent errors
  if (error instanceof SilentError) return;

  const notification: PartialBy<INotification, 'key'> = {
    content: normalizeErrorMessage(error),
    type: options.type,
    duration: options.duration,
    mode: options.mode,
    title: options.title,
  };

  if (options.sequential) {
    dispatch(addSequentialNotification(notification));
  } else {
    dispatch(addNotification(notification));
  }
};

export const getErrorSeverityType = (error: any): NotificationType => {
  const statusCode = getStatusCode(error);

  switch (statusCode) {
    case HTTPStatusCode.SERVICE_UNAVAILABLE:
    case HTTPStatusCode.UNDEPLOYED_PIPELINE_ERROR_STATUS:
      return NotificationType.Warning;
    default:
      return NotificationType.Error;
  }
};

export const shouldSilenceHttpError = (error: unknown): boolean => {
  const statusCode = getStatusCode(error);

  if (isNil(statusCode)) return false;

  const isSilentServerError = SILENT_5XX_ERRORS && statusCode >= 500 && statusCode < 600;
  const isSilent404Error = SILENT_404_ERRORS && statusCode === HTTPStatusCode.NOT_FOUND;

  return isSilentServerError || isSilent404Error;
};
